import { Component, OnInit, OnDestroy } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/shared/services/auth';
import {
  BoardUser,
  BoardUserData,
} from '../up-board-editor/up-board-editor.component';
import { Board, Employee } from '../up-board/up-board.component';
import { BoardConfig } from 'src/app/shared/services/tutorial.service';
import * as icons from '@fortawesome/free-solid-svg-icons';
import { BoardPermissionsService } from 'src/app/shared/services/board-permissions.service';
declare var $: any;

// Channel eligibility types
type ChannelType = 'phone' | 'online';

interface ChannelState {
  state: string;
  order: number;
  eligible: boolean;
}

interface EmployeeChannels {
  instore?: ChannelState;
  phone?: ChannelState;
  online?: ChannelState;
}

@Component({
  selector: 'app-up-board-admin-users',
  templateUrl: './up-board-admin-users.component.html',
  styleUrls: ['./up-board-admin-users.component.css'],
})
export class UpBoardAdminUsersComponent implements OnInit, OnDestroy {
  icons = icons;
  invited: string[] = [];
  users: (BoardUserData & { email: string })[] = [];
  originalUsers: Map<string, BoardUserData> = new Map();
  hasUnsavedChanges: boolean = false;
  isSaving: boolean = false;
   showDiscardConfirmModal: boolean = false;
   
   // Board config for channel visibility
   boardConfig: BoardConfig | null = null;
   private boardConfigSubscription: Subscription | null = null;
   private usersSubscription: Subscription | null = null;
   private boardSubscription: Subscription | null = null;
  
  new_user: BoardUser = {
    email: '',
    data: {
      email: '',
      name: '',
      phone: '',
      photoUrl: '',
      state: '',
      order: 0,
      roles: {
        admin: false,
        board_manager: false,
        salesman: false,
      },
    },
  };
  board: Board | undefined;
  boardId: string = '';
  boardUser: BoardUserData | null = null;
  modal_error: string | undefined;
  modal_user?: BoardUserData;
  modal_add_error: string | undefined;
  showAddUserModal: boolean = false;

  constructor(
    public db2: AngularFirestore,
    public auth: AngularFireAuth,
    public authService: AuthService,
    private route: ActivatedRoute,
    public boardPermissions: BoardPermissionsService
  ) {}

  openAddUserModal() {
    this.resetNewUserForm();
    this.modal_add_error = undefined;
    this.showAddUserModal = true;
  }

  closeAddUserModal() {
    this.showAddUserModal = false;
    this.resetNewUserForm();
    this.modal_add_error = undefined;
  }

  resetNewUserForm() {
    this.new_user = {
      email: '',
      data: {
        email: '',
        name: '',
        phone: '',
        photoUrl: '',
        state: '',
        order: 0,
        roles: {
          admin: false,
          board_manager: false,
          salesman: false,
        },
      },
    };
  }

  async submitAddUser() {
    if (!this.new_user.email || !this.new_user.data.name) {
      this.modal_add_error = 'Please fill in email and name';
      return;
    }

    try {
      await this.upsertBoardUser(this.new_user);
      this.closeAddUserModal();
    } catch (error) {
      this.modal_add_error = 'Error adding user. Please try again.';
      console.error(error);
    }
  }

  async submitAddSalesman() {
    if (!this.new_user.email || !this.new_user.data.name) {
      this.modal_add_error = 'Please fill in email and name';
      return;
    }

    try {
      await this.requestAddSalesman(this.new_user);
      this.closeAddUserModal();
    } catch (error) {
      this.modal_add_error = 'Error submitting request. Please try again.';
      console.error(error);
    }
  }

  async removeUser(user: BoardUserData | undefined) {
    if (!user) return;

    await this.db2
      .collection('boards')
      .doc(this.boardId)
      .collection('users')
      .doc(user.email)
      .delete();

    $('#modal_delete_user').modal('hide');
  }

  formatPhoneNumber(str: string) {
    //Filter only numbers from the input
    let cleaned = ('' + str).replace(/\D/g, '');

    //Check if the input is of correct
    let match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);

    if (match) {
      //Remove the matched extension code
      //Change this to format for any country code.
      let intlCode = match[1] ? `${match[1]}` : '1';
      return ['+', intlCode, match[2], match[3], match[4]].join('');
    }

    return undefined;
  }

  async requestAddSalesman(user: BoardUser) {
    user.data.roles.salesman = true;
    user.data.roles.admin = false;
    user.data.roles.board_manager = false;

    var phone = this.formatPhoneNumber(user.data.phone) || '';
    user.data.phone = phone;

    await this.db2
      .collection('boards')
      .doc(this.boardId)
      .collection('add_user_requests')
      .add(user);
  }

  async upsertBoardUser(user: BoardUser) {
    var phone = this.formatPhoneNumber(user.data.phone) || '';

    user.data.phone = phone;
    user.data.email = user.email;

    if (!user.data.roles.salesman) {
      user.data.state = 'inactive';
    }

    await this.db2
      .collection('boards')
      .doc(this.boardId)
      .collection('users')
      .doc<BoardUserData>(user.email)
      .set(user.data);
  }

  /**
   * Track changes in user data by comparing current state with original state
   */
  private trackChanges(): void {
    let hasChanges = false;

    for (const user of this.users) {
      const original = this.originalUsers.get(user.email);
      if (!original || !this.isUserDataEqual(original, user)) {
        hasChanges = true;
        break;
      }
    }

    this.hasUnsavedChanges = hasChanges;
  }

   /**
    * Deep comparison of user data objects
    * Made public so it can be called from the template to highlight modified rows
    */
   isUserDataEqual(user1: BoardUserData, user2: BoardUserData): boolean {
     return (
       user1.name === user2.name &&
       user1.phone === user2.phone &&
       user1.roles.admin === user2.roles.admin &&
       user1.roles.board_manager === user2.roles.board_manager &&
       user1.roles.salesman === user2.roles.salesman &&
       JSON.stringify((user1 as any).channels) === JSON.stringify((user2 as any).channels)
     );
   }

   /**
    * Save all modified users to the database
    */
   async saveAllChanges(): Promise<void> {
     if (this.isSaving || !this.hasUnsavedChanges) {
       return;
     }

     this.isSaving = true;
     try {
       const updates: Promise<void>[] = [];

       for (const user of this.users) {
         const original = this.originalUsers.get(user.email);
         
         // Only save if user data has changed
         if (!original || !this.isUserDataEqual(original, user)) {
           updates.push(
             this.db2
               .collection('boards')
               .doc(this.boardId)
               .collection('users')
               .doc<BoardUserData>(user.email)
               .set(user, { merge: true })
           );
         }
       }

       // Wait for all updates to complete
       await Promise.all(updates);

       // Update original users with current state using deep copy
       this.users.forEach((user) => {
         this.originalUsers.set(user.email, JSON.parse(JSON.stringify(user)));
       });

       this.hasUnsavedChanges = false;
     } catch (error) {
       console.error('Error saving changes:', error);
       alert('Failed to save changes. Please try again.');
     } finally {
       this.isSaving = false;
     }
   }

  /**
   * Discard all unsaved changes and revert to original state
   */
  discardChanges(): void {
    if (!this.hasUnsavedChanges) {
      return;
    }
    
    this.showDiscardConfirmModal = true;
  }

  /**
   * Confirm discard and revert all changes
   */
  confirmDiscardChanges(): void {
    // Revert users to original state using deep copy
    this.users = this.users.map((user) => {
      const original = this.originalUsers.get(user.email);
      return original ? JSON.parse(JSON.stringify(original)) : user;
    });

    this.hasUnsavedChanges = false;
    this.showDiscardConfirmModal = false;
  }

  /**
   * Cancel discard operation
   */
  cancelDiscardChanges(): void {
    this.showDiscardConfirmModal = false;
  }

  /**
   * Called whenever user data changes - updates change tracking
   */
  onUserChange(): void {
    this.trackChanges();
  }

  sort(users: (BoardUserData&{ email: string; })[]): (BoardUserData&{ email: string; })[] {
    return users.sort((a, b) => {
      // sort alphabetically on name
      const an = a.name || a.email || '';
      const bn = b.name || b.email || '';
      return an.localeCompare(bn);
    });
  }

  ngOnInit(): void {
    var sub = this.auth.user.subscribe((u) => {
      sub.unsubscribe();

      if (!u || !u.email) return;
      if (!this.route.parent) return;

      let boardId = this.route.parent.snapshot.paramMap.get('id');

      if (!boardId) return;
      this.boardId = boardId;
      this.boardPermissions.init(this.boardId, u.email);

      this.usersSubscription = this.db2
        .collection('boards')
        .doc(this.boardId)
        .collection<BoardUserData>('users')
        .valueChanges({ idField: 'email' })
        .subscribe((usrs) => {
          // Check if this is the initial load by seeing if originalUsers is empty
          const isInitialLoad = this.originalUsers.size === 0;
          
          this.users = usrs;
          
          // Only initialize original users map on first load
          // This prevents resetting change tracking on subsequent Firestore updates
          if (isInitialLoad) {
            usrs.forEach((user) => {
              // Deep copy the user object to preserve the original state
              this.originalUsers.set(user.email, JSON.parse(JSON.stringify(user)));
            });
          }
          
          this.trackChanges();
        });

      this.boardSubscription = this.db2
        .collection('boards')
        .doc<any>(this.boardId)
        .valueChanges()
        .subscribe((b) => {
          this.board = b.data;
          // Extract board config for channel visibility
          this.boardConfig = b.config || null;
        });
    });
  }

  ngOnDestroy(): void {
    if (this.boardConfigSubscription) {
      this.boardConfigSubscription.unsubscribe();
    }
    if (this.usersSubscription) {
      this.usersSubscription.unsubscribe();
    }
    if (this.boardSubscription) {
      this.boardSubscription.unsubscribe();
    }
  }

  /**
   * Get the display name for a channel
   */
  getChannelDisplayName(channel: ChannelType): string {
    return channel === 'phone' ? 'Phone' : 'Internet';
  }

  /**
   * Check if employee is eligible for a specific channel
   */
  isChannelEligible(user: BoardUserData & { email: string }, channel: ChannelType): boolean {
    const channels = (user as any).channels as EmployeeChannels | undefined;
    if (!channels || !channels[channel]) {
      // Default to eligible if no channel data exists (legacy data)
      return true;
    }
    return channels[channel]?.eligible !== false;
  }

   /**
    * Check if employee is currently in a channel's queue (eligible and has state)
    * Note: 'inactive' is a global state only - channel eligibility is controlled by the eligible flag
    */
   isEmployeeInChannelQueue(user: BoardUserData & { email: string }, channel: ChannelType): boolean {
     const channels = (user as any).channels as EmployeeChannels | undefined;
     if (!channels || !channels[channel]) {
       return false;
     }
     const channelData = channels[channel];
     // In queue if eligible and has a valid state
     return channelData?.eligible === true && !!channelData?.state;
   }

   /**
    * Handle eligibility toggle from the UI - directly toggle like SALESMAN column
    */
   toggleEligibilityDirect(user: BoardUserData & { email: string }, channel: ChannelType, event: Event): void {
     const currentlyEligible = this.isChannelEligible(user, channel);
     this.toggleEligibilityLocal(user, channel, !currentlyEligible);
     this.onUserChange();
   }

   /**
    * Toggle eligibility locally without saving to database
    */
   private toggleEligibilityLocal(user: BoardUserData & { email: string }, channel: ChannelType, enable: boolean): void {
     if (!user) return;
     
     // Initialize channels object if it doesn't exist
     if (!(user as any).channels) {
       (user as any).channels = {};
     }
     
     // Initialize channel if it doesn't exist
     if (!(user as any).channels[channel]) {
       (user as any).channels[channel] = {
         state: 'available',
         order: 0,
         eligible: false
       };
     }
     
     if (enable) {
       // Enable eligibility - set to available and add to queue
       const maxOrder = this.getMaxOrderForChannel(channel);
       (user as any).channels[channel].eligible = true;
       (user as any).channels[channel].state = 'available';
       (user as any).channels[channel].order = maxOrder + 1;
     } else {
       // Disable eligibility - mark as not eligible but keep 'available' state
       // Note: 'inactive' is a global state only - eligibility is controlled by the eligible flag
       (user as any).channels[channel].eligible = false;
       (user as any).channels[channel].state = 'available';
       (user as any).channels[channel].order = 0;
     }
   }

   /**
    * Enable eligibility for an employee in a channel
    * Employee joins at the bottom of the queue
    */
   async enableEligibility(user: BoardUserData & { email: string }, channel: ChannelType): Promise<void> {
     // Get current max order for this channel
     const maxOrder = this.getMaxOrderForChannel(channel);
     
     const update: { [key: string]: any } = {
       [`channels.${channel}.eligible`]: true,
       [`channels.${channel}.state`]: 'available',
       [`channels.${channel}.order`]: maxOrder + 1
     };
     
     await this.db2.collection('boards').doc(this.boardId)
       .collection('users').doc(user.email)
       .update(update);
   }

   /**
    * Disable eligibility for an employee in a channel
    * Removes them from the queue by setting eligible: false
    * Note: 'inactive' is a global state only - we keep state as 'available'
    */
   async disableEligibility(user: BoardUserData & { email: string }, channel: ChannelType): Promise<void> {
     const update: { [key: string]: any } = {
       [`channels.${channel}.eligible`]: false,
       [`channels.${channel}.state`]: 'available',
       [`channels.${channel}.order`]: 0
     };
     
     await this.db2.collection('boards').doc(this.boardId)
       .collection('users').doc(user.email)
       .update(update);
   }

  /**
   * Get the maximum order value for a channel across all users
   */
  getMaxOrderForChannel(channel: ChannelType): number {
    let maxOrder = 0;
    
    for (const user of this.users) {
      const channels = (user as any).channels as EmployeeChannels | undefined;
      const order = channels?.[channel]?.order;
      if (order !== undefined && order > maxOrder) {
        maxOrder = order;
      }
    }
    
    return maxOrder;
  }

  // ============================================================
  // Story 3.7 Stub Methods - TODO: Implement properly in Story 3.7
  // These stubs exist to allow Story 3.7 tests to compile
  // ============================================================

  /** Whether to show eligibility warning dialog */
  showEligibilityWarning = false;

  /** Message to display in eligibility warning dialog */
  eligibilityWarningMessage = '';

  /** User being warned about eligibility change */
  private pendingEligibilityUser: (BoardUserData & { email: string }) | null = null;

  /** Channel being changed for eligibility warning */
  private pendingEligibilityChannel: ChannelType | null = null;

  /**
   * Check if employee is busy with instore customer
   */
  isEmployeeBusyInstore(user: BoardUserData & { email: string }): boolean {
    const state = user.state;
    return state === 'busy' || state === 'customer';
  }

  /**
   * Check if employee has a queue position in a channel
   * Note: 'inactive' is a global state only - eligibility is controlled by the eligible flag
   */
  hasQueuePosition(user: BoardUserData & { email: string }, channel: string): boolean {
    const channels = (user as any).channels as EmployeeChannels | undefined;
    if (!channels || !channels[channel as ChannelType]) {
      return false;
    }
    const channelState = channels[channel as ChannelType];
    // Has queue position if eligible and has order > 0
    return channelState?.eligible === true && !!channelState?.state && (channelState.order ?? 0) > 0;
  }

  /**
   * Show warning dialog before disabling eligibility for active employee
   */
  showEligibilityWarningDialog(user: BoardUserData & { email: string }, channel: string, type: 'active' | 'busy'): void {
    this.pendingEligibilityUser = user;
    this.pendingEligibilityChannel = channel as ChannelType;
    const channelName = this.getChannelDisplayName(channel as ChannelType);
    
    if (type === 'active') {
      this.eligibilityWarningMessage = `${user.name || user.email} is currently in the ${channelName} queue. Disabling eligibility will remove them from the queue.`;
    } else {
      this.eligibilityWarningMessage = `${user.name || user.email} is currently busy with an instore customer. Disabling eligibility will affect their status.`;
    }
    
    this.showEligibilityWarning = true;
  }

  /**
   * Cancel eligibility change and close warning dialog
   */
  cancelEligibilityChange(): void {
    this.showEligibilityWarning = false;
    this.eligibilityWarningMessage = '';
    this.pendingEligibilityUser = null;
    this.pendingEligibilityChannel = null;
  }

  /**
   * Handle eligibility toggle with validation
   */
  onEligibilityToggle(user: BoardUserData & { email: string }, channel: string, event: Event): void {
    const currentlyEligible = this.isChannelEligible(user, channel as ChannelType);
    
    // If disabling eligibility, check for warnings
    if (currentlyEligible) {
      if (this.hasQueuePosition(user, channel)) {
        this.showEligibilityWarningDialog(user, channel, 'active');
        return;
      }
      if (this.isEmployeeBusyInstore(user)) {
        this.showEligibilityWarningDialog(user, channel, 'busy');
        return;
      }
    }
    
    // No warning needed, proceed with toggle
    this.toggleEligibilityDirect(user, channel as ChannelType, event);
  }

  /**
   * Confirm disabling eligibility after warning
   */
  confirmDisableEligibility(): void {
    if (this.pendingEligibilityUser && this.pendingEligibilityChannel) {
      this.toggleEligibilityLocal(this.pendingEligibilityUser, this.pendingEligibilityChannel, false);
      this.onUserChange();
    }
    this.cancelEligibilityChange();
  }
}
